import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiRouteConstants } from '../../../../../../core/constants/api-routes';
import { ValidationResult } from '../../../../../../core/services/validation/models/validation-result';
import { CoordinationParametersResponse } from '../../../coordination-parameters/models/coordination-parameters-response';
import { CoordinationParametersRequest } from '../../../coordination-parameters/models/coordination-parameters-request';
import { CoordinationResultsRequest } from '../../../coordination-results/models/coordination-results-request';
import { CoordinationResultsResponse } from '../../../coordination-results/models/coordination-results-response';
import { CoordinateApplicationResponse } from './models/coordinate-application-response';
import { CoordinateApplicationRequest } from './models/coordinate-application-request';
import { CoordinationResultStudyDetailResponse } from '../../../coordination-results/models/coordination-result-study-detail-response';
import { CoordinationResultStudyDetailRequest } from '../../../coordination-results/models/coordination-result-study-detail-request';
import { CoordinationSubmissionResponse } from '../../../coordination-results/models/coordination-submission-response';
import { CoordinationStatusResponse } from '../../../coordination-results/models/coordination-status-response';
import { ApplicationCoordinationStatusService } from '../../../../shared/services/application-coordination-status/application-coordination-status.service';

@Injectable({
    providedIn: 'root',
})
export class CoordinationService {
    private readonly applicationApiRoute = ApiRouteConstants.Application;

    constructor(
        private readonly http: HttpClient,
        private readonly applicationCoordinationStatusService: ApplicationCoordinationStatusService,
    ) {}

    getCoordinationParameters(
        applicationId: string,
    ): Observable<CoordinationParametersResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationParameters',
                applicationId,
            );

        return this.http.get<CoordinationParametersResponse>(fullRoute);
    }

    saveCoordinationParameters(
        request: CoordinationParametersRequest,
    ): Observable<ValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationParameters',
                request.applicationId,
            );

        return this.http.post<ValidationResult>(fullRoute, request);
    }

    getCoordinationResults(
        applicationId: string,
    ): Observable<CoordinationResultsResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationResults',
                applicationId,
            );

        return this.http.get<CoordinationResultsResponse>(fullRoute);
    }

    saveCoordinationResults(
        request: CoordinationResultsRequest,
    ): Observable<ValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationResults',
                request.applicationId,
            );

        return this.http.post<ValidationResult>(fullRoute, request);
    }

    getCoordinationResultStudyDetails(
        request: CoordinationResultStudyDetailRequest,
    ): Observable<CoordinationResultStudyDetailResponse> {
        const fullRoute =
            this.applicationApiRoute.getCoordinationResultStudyDetailsRoute(
                request.applicationId,
                request.studyId,
            );

        return this.http.get<CoordinationResultStudyDetailResponse>(fullRoute);
    }

    getCoordinationStatus(
        applicationId: string,
    ): Observable<CoordinationStatusResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationStatus',
                applicationId,
            );

        return this.http.get<CoordinationStatusResponse>(fullRoute).pipe(
            tap((applicationCoordinationStatus) => {
                this.applicationCoordinationStatusService.setApplicationCoordinationStatus(
                    applicationCoordinationStatus,
                );
            }),
        );
    }

    submitForCoordination(
        request: CoordinateApplicationRequest,
    ): Observable<CoordinateApplicationResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitForCoordination',
                request.applicationId,
            );

        return this.http.post<CoordinateApplicationResponse>(
            fullRoute,
            request,
        );
    }

    submitToEwa(
        applicationId: string,
    ): Observable<CoordinationSubmissionResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'coordinationSubmitToEwa',
                applicationId,
            );

        return this.http.post<CoordinationSubmissionResponse>(fullRoute, {});
    }
}
